import React, { useState, useEffect } from 'react';
import SitePageBody, { leadingHeading } from '../components/common/SitePageBody';
import { useBranding } from '../context/BrandingContext';
import { getSitePage } from '../services/api';
import SEO from '../components/SEO';
import FroggySpinner from '../components/common/FroggySpinner';
import { FULL_KEYWORDS } from '../constants/seoKeywords';
import '../styles/about-us.css';

const AboutUsPage = () => {
  const { getTemplateAsset, getTemplateCopy, template } = useBranding();
  const [content, setContent] = useState(null);
  const [title, setTitle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true);
        const data = await getSitePage('about-us');
        setContent(data.content_md);
        setTitle(data.title);
        setError(false);
      } catch (err) {
        console.error('Failed to fetch about us content:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  if (loading) {
    return (
      <div className="about-us-page">
        <div className="about-us-header" style={{ textAlign: 'center' }}>
          <FroggySpinner size="lg" label="Loading about us" />
        </div>
      </div>
    );
  }

  if (error || !content) {
    return (
      <div className="about-us-page">
        <div className="about-us-header">
          <img 
            src={process.env.PUBLIC_URL + '/logos/ff_original_320px.png'} 
            alt={template?.name || 'Feeling Froggy Logo'}
            className="about-us-logo"
          />
          <h1 className="about-us-title">
            About {template?.name || 'Feeling Froggy'}
          </h1>
        </div>
        <div className="about-us-content">
          <p className="about-us-description">
            This content is temporarily unavailable. Please try again later.
          </p>
        </div>
      </div>
    );
  }
  
  // Batch 34 C2: the SitePage's title is "About Us" and its body opens
  // "# About Feeling Froggy" — not a repeat, a RESTATEMENT of the title, and
  // the customer read both, one under the other. The page keeps ONE h1: the
  // body's own heading when it is an "About …" restatement (the fuller,
  // operator-written copy), which SitePageBody then does not render twice.
  const lead = leadingHeading(content);
  const fallbackTitle = title || `About ${template?.name || 'Feeling Froggy'}`;
  const pageTitle = lead && /^about\b/i.test(lead.text) && /^about\b/i.test(fallbackTitle)
    ? lead.text
    : fallbackTitle;

  return (
    <>
      <SEO
        title="About Us — Feeling Froggy"
        description="Poison dart frogs, fire-bellied toads, mossy frogs, and vivarium plants from a trusted captive-bred source. Live-arrival guarantee."
        canonical="https://feelingfroggy.llc/about"
        keywords={FULL_KEYWORDS}
      />
      <div className="about-us-page">
        <div className="about-us-header">
          <img
            src={process.env.PUBLIC_URL + '/logos/ff_original_320px.png'}
            alt={template?.name || 'Feeling Froggy Logo'}
            className="about-us-logo"
          />
          <h1 className="about-us-title">{pageTitle}</h1>
        </div>

        <div className="about-us-content">
          <SitePageBody className="about-us-markdown" title={pageTitle} content={content} />

          <div className="about-us-contact contact-block">
            {template?.footerLinks?.map((link, index) => (
              <div key={index} className="about-us-contact-item">
                <a
                  href={link.href}
                  className="about-us-contact-link"
                >
                  {link.label}
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default AboutUsPage;